import {
    AIModelType,
    classifyChecklistWriteError,
    ClauseChecklistRefreshUnconfirmedError,
    getAllOrganizations,
    IClauseChecklist,
    IClauseChecklistUpdate,
    IOrganizationMinimal,
    useUpdateClauseChecklist,
} from "@/api";
import { ChecklistGroupField } from "@/components/admin/dialogs/clause-checklists/ChecklistGroupField";
import type { EntityComboboxValidity } from "@/components/shared/EntityCombobox";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { ORGANIZATION_QUERY_FILTER } from "@/config/constants";
import {
    Autocomplete,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    TextField as MuiTextField,
    Stack,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useId, useState } from "react";

interface EditClauseChecklistDialogProps {
    /**
     * Whether the dialog is open.
     */
    open: boolean;
    /**
     * Handler to close the dialog.
     */
    onClose: () => void;
    /**
     * Clause Checklist to edit.
     */
    clauseChecklist: IClauseChecklist;
}

const FORM_REFUSED =
    "The checklist couldn't be saved. Check the details and try again.";
const UPDATE_FAILED = "Failed to update checklist.";
const UPDATE_UNCONFIRMED =
    "The checklist was updated, but some views couldn't refresh.";
const ORGANIZATION_REMOVAL_HELP =
    "To remove an organization, open its page and remove this checklist from its checklists.";

/**
 * Dialog to edit a clause checklist.
 */
export const EditClauseChecklistDialog = ({
    open,
    onClose,
    clauseChecklist,
}: EditClauseChecklistDialogProps) => {
    const [name, setName] = useState(clauseChecklist.name);
    const [description, setDescription] = useState(clauseChecklist.description);
    const assignedOrganizations = clauseChecklist.organizations;
    const assignedLabelId = useId();
    const [organization, setOrganization] =
        useState<IOrganizationMinimal | null>(null);
    const [modelType, setModelType] = useState<AIModelType>(
        clauseChecklist.model_type
    );
    const [showGaps, setShowGaps] = useState(clauseChecklist.show_gaps ?? true);
    const initialGroupId = clauseChecklist.organization_group?.id ?? null;
    const [groupId, setGroupId] = useState<number | null>(initialGroupId);
    const [groupValidity, setGroupValidity] =
        useState<EntityComboboxValidity>("empty");
    const [refusedGroupId, setRefusedGroupId] = useState<number | null>(null);
    const [failure, setFailure] = useState<string | null>(null);
    const [organizationSearch, setOrganizationSearch] = useState("");

    // Fetch organizations for autocomplete
    const { data: organizationsData, isPending: organizationsLoading } =
        useQuery({
            queryKey: ["organizations-autocomplete", organizationSearch],
            queryFn: () =>
                getAllOrganizations(1, 100, {
                    ...ORGANIZATION_QUERY_FILTER,
                    ...(organizationSearch ? { q: organizationSearch } : {}),
                }),
        });

    const organizations = (organizationsData?.items ?? []).filter(
        (option) =>
            !assignedOrganizations.some((assigned) => assigned.id === option.id)
    );

    const mutation = useUpdateClauseChecklist(clauseChecklist.id);

    // Reset form when dialog opens with new data
    useEffect(() => {
        if (open) {
            setName(clauseChecklist.name);
            setDescription(clauseChecklist.description);
            setOrganization(null);
            setModelType(clauseChecklist.model_type);
            setShowGaps(clauseChecklist.show_gaps ?? true);
            setGroupId(clauseChecklist.organization_group?.id ?? null);
            setRefusedGroupId(null);
            setFailure(null);
        }
    }, [open, clauseChecklist]);

    const handleClose = () => {
        onClose();
        mutation.reset();
    };

    const groupRefused = refusedGroupId !== null && refusedGroupId === groupId;
    const isValid =
        name.trim() !== "" &&
        description.trim() !== "" &&
        (assignedOrganizations.length > 0 ||
            organization !== null ||
            groupId !== null) &&
        (groupId === null || groupValidity === "valid") &&
        !groupRefused;

    const handleSave = () => {
        if (!isValid) return;

        const update: IClauseChecklistUpdate = {
            name: name.trim(),
            description: description.trim(),
            model_type: modelType,
            show_gaps: showGaps,
        };
        if (organization) {
            update.organization_ids = [organization.id];
        }
        if (groupId !== initialGroupId) {
            update.organization_group_id = groupId;
        }

        setFailure(null);
        setRefusedGroupId(null);
        mutation.mutate(update, {
            onSuccess: () => {
                handleClose();
                enqueueSnackbar("Checklist updated successfully", {
                    variant: "success",
                });
            },
            onError: (error) => {
                if (error instanceof ClauseChecklistRefreshUnconfirmedError) {
                    handleClose();
                    enqueueSnackbar(UPDATE_UNCONFIRMED, { variant: "warning" });
                    return;
                }
                const failure = classifyChecklistWriteError(error, {
                    organization_group_id: update.organization_group_id,
                });
                if (failure.kind === "group_refused") {
                    setRefusedGroupId(failure.targetId);
                } else if (
                    failure.kind === "validation" &&
                    failure.status === 422
                ) {
                    setFailure(FORM_REFUSED);
                } else if (failure.kind === "validation" && failure.detail) {
                    setFailure(failure.detail);
                } else {
                    setFailure(UPDATE_FAILED);
                }
            },
        });
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Edit Checklist"
            subtitle="Update the checklist information"
            isLoading={mutation.isPending}
            actions={[
                {
                    label: "Save",
                    onClick: handleSave,
                    variant: "contained",
                    color: "primary",
                    disabled: !isValid || mutation.isPending,
                },
            ]}
            footerText={
                failure && (
                    <Typography
                        variant="caption"
                        color="error"
                        role="alert"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {failure}
                    </Typography>
                )
            }
        >
            <Stack spacing={3}>
                <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={failure !== null}
                    required
                    fullWidth
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    fullWidth
                    multiline
                    rows={3}
                />
                {assignedOrganizations.length > 0 && (
                    <Stack
                        role="group"
                        aria-labelledby={assignedLabelId}
                        spacing={0.5}
                    >
                        <Typography
                            id={assignedLabelId}
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                        >
                            Assigned organizations
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, minWidth: 0 }}>
                            {assignedOrganizations.map((assigned) => (
                                <Typography
                                    key={assigned.id}
                                    component="li"
                                    variant="body2"
                                    sx={{ overflowWrap: "anywhere" }}
                                >
                                    {assigned.name}
                                </Typography>
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {ORGANIZATION_REMOVAL_HELP}
                        </Typography>
                    </Stack>
                )}
                <Autocomplete
                    value={organization}
                    onChange={(_, newValue) => setOrganization(newValue)}
                    options={organizations}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    loading={organizationsLoading}
                    onInputChange={(_, value) => setOrganizationSearch(value)}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                        <MuiTextField
                            {...params}
                            label={
                                assignedOrganizations.length > 0
                                    ? "Add organization"
                                    : "Organization"
                            }
                            required={
                                assignedOrganizations.length === 0 &&
                                groupId === null
                            }
                            size="small"
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {organizationsLoading && (
                                                <CircularProgress size={20} />
                                            )}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
                <ChecklistGroupField
                    value={groupId}
                    onChange={setGroupId}
                    onValidityChange={setGroupValidity}
                    refused={groupRefused}
                    disabled={mutation.isPending}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showGaps}
                            onChange={(e) => setShowGaps(e.target.checked)}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">
                                Check for Coverage Gaps
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                When disabled, gap information is hidden from
                                topic summaries, headlines, and the scope
                                overview
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: "flex-start" }}
                />
            </Stack>
        </Modal>
    );
};
